import { Box, Tooltip } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import Iconify from 'src/components/iconify';
import Label from 'src/components/label';
import AchievementStatusBadge from 'src/modules/trails/components/achievement-status-badge';

type GetEventsColumnsParams = {
  formatDate: (value?: string | Date | null) => string;
};

const isEmptyDateValue = (value: unknown) => {
  if (value === null || value === undefined) {
    return true;
  }

  const normalizedValue = String(value).trim().toLowerCase();

  return normalizedValue === '' || normalizedValue === 'null' || normalizedValue === 'undefined';
};

const renderDateCell = (value: unknown, formatDate: (value?: string | Date | null) => string) => {
  if (isEmptyDateValue(value) || Number.isNaN(new Date(String(value)).getTime())) {
    return (
      <Label
        variant="soft"
        color="default"
        sx={{
          border: '1px solid',
          borderColor: 'grey.300',
          color: 'text.secondary',
          fontWeight: 700,
        }}
      >
        Sem data
      </Label>
    );
  }

  return (
    <Box sx={{ width: 1, display: 'flex', justifyContent: 'flex-start' }}>
      <Label
        variant="outlined"
        sx={{
          gap: 0.75,
          px: 1.4,
          py: 0.45,
          display: 'inline-flex',
          alignItems: 'center',
          fontWeight: 700,
        }}
      >
        <Iconify icon="mdi:calendar-month-outline" sx={{ width: 16, height: 16 }} />
        {formatDate(String(value))}
      </Label>
    </Box>
  );
};

export const getEventsColumns = ({ formatDate }: GetEventsColumnsParams): GridColDef[] => {
  const columns: GridColDef[] = [
    {
      field: 'image',
      headerName: 'Imagem',
      width: 112,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => {
        const image = String(params.value || '');

        if (!image) {
          return '-';
        }

        return (
          <Tooltip
            arrow
            placement="left"
            title={
              <Box
                component="img"
                src={image}
                alt={String(params.row?.name || 'Experiência')}
                sx={{
                  display: 'block',
                  width: 240,
                  maxHeight: 240,
                  objectFit: 'contain',
                  borderRadius: 0.75,
                }}
              />
            }
            slotProps={{
              tooltip: {
                sx: {
                  p: 0.5,
                  bgcolor: 'common.white',
                  boxShadow: 8,
                },
              },
            }}
          >
            <Box
              component="img"
              src={image}
              alt={String(params.row?.name || 'Experiência')}
              sx={{
                display: 'block',
                width: 72,
                height: 40,
                borderRadius: 0.75,
                objectFit: 'cover',
                border: '1px solid',
                borderColor: 'grey.300',
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'name',
      headerName: 'Nome',
      flex: 1.4,
      minWidth: 280,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'startDate',
      headerName: 'Data de início',
      flex: 0.8,
      minWidth: 170,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams) => renderDateCell(params.value, formatDate),
    },
    {
      field: 'endDate',
      headerName: 'Data de fim',
      flex: 0.8,
      minWidth: 170,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams) => renderDateCell(params.value, formatDate),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.7,
      minWidth: 160,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ width: 1, display: 'flex', justifyContent: 'flex-start' }}>
          <AchievementStatusBadge
            status={String(params.value || '')}
            label={params.row?.statusLabel}
            variant="soft"
          />
        </Box>
      ),
    },
  ];

  return columns.map((column) => ({
    ...column,
    disableColumnMenu: true,
  }));
};
